import { groupColor, teamEloFromSlot } from '../../engine';
import { useT } from '../../i18n';
import { ReplacementBadge } from '../players';

/** A single slot within a team — either a concrete player or a placeholder referencing a group standing. */
interface MatchupSlot {
  kind?: string;
  fromGroup?: string | number;
  fromPosition?: string | number;
  playerId?: string;
  originGroup?: string;
  isReplacement?: boolean;
  originalCompletedCount?: number;
  replacementAfterMatch?: number | null;
  replacedPlayerId?: string;
  replacementTimestamp?: number | string | null;
  replacementPhase?: string | number;
  replacementRound?: number | string;
  replacementTotalMatches?: number;
  firstMatchAsReplacement?: {
    matchNumber?: number | string;
    phase?: string | number;
    round?: number | string;
    court?: number | string;
    time?: string;
    team1Names?: string;
    team2Names?: string;
    date?: string;
  } | null;
}

/** A team consisting of two slots resolved against the player list. */
interface MatchupTeam {
  p1: MatchupSlot;
  p2: MatchupSlot;
}

/** Optional score block; when present the score is shown in place of «VS», with pre-match team-ELO and ELO-change pills. */
interface MatchupScore {
  s1?: number | null;
  s2?: number | null;
  teamElo1Pre?: number | null;
  teamElo2Pre?: number | null;
  eloChange1?: number | null;
  eloChange2?: number | null;
  t1NewbieMul?: number | null;
  t2NewbieMul?: number | null;
}

export interface MatchupLinesProps {
  /** Home team; its `p1`/`p2` slots are resolved against `players`. */
  team1: MatchupTeam;
  /** Away team; its `p1`/`p2` slots are resolved against `players`. */
  team2: MatchupTeam;
  /** Player list used to resolve slot player ids and compute team ELO. Permissive
   *  shape so both the domain `Player` and lighter `{id,name,elo}` lists pass. */
  players?: Array<{ id?: string; name?: string; elo?: number | string }>;
  /** Optional click handler; when provided each concrete player renders as a button calling this with the player id. */
  onPlayerClick?: (playerId: string) => void;
  /** Layout variant: 'sm' (previous/next) or 'md' (ongoing). */
  size?: 'sm' | 'md';
  /** When set, the score is shown instead of «VS», keeping team-ELO on the sides. */
  score?: MatchupScore | null;
  /** Invert the palette for a light background. */
  light?: boolean;
}

// With combined team-ELO between the names and VS (or score if the match is finished)
//
// size: 'sm' (previous/next), 'md' (ongoing)
// score: { s1, s2 } — if set, the score is shown instead of VS
/**
 * Matchup header with both teams' players, combined team-ELO, and a centre VS-or-score block.
 * Extracted verbatim from the original artifact (legacy/App.jsx); markup/text unchanged so styling is identical.
 */
export function MatchupLines({
  team1,
  team2,
  players,
  onPlayerClick,
  size = 'md',
  score = null,
  light = false,
}: MatchupLinesProps) {
  const t = useT();
  // Use stored pre-match ELO if available (after the match has been played),
  // otherwise the current ELO (for upcoming matches)
  const liveT1 = teamEloFromSlot(team1, players);
  const liveT2 = teamEloFromSlot(team2, players);
  const t1Sum = score && score.teamElo1Pre != null ? score.teamElo1Pre : liveT1;
  const t2Sum = score && score.teamElo2Pre != null ? score.teamElo2Pre : liveT2;

  const isSmall = size === 'sm';
  const nameClass = isSmall ? 'text-sm font-medium' : 'text-lg font-semibold';
  const eloClass = isSmall ? 'text-sm' : 'text-xl';
  const vsClass = isSmall ? 'text-2xl' : 'text-4xl';
  const scoreClass = isSmall ? 'text-xl' : 'text-4xl';
  // 9 px is the floor for text (#1144): the small variant no longer shrinks it
  const labelClass = 'text-[9px]';
  const sideLabelClass = isSmall ? 'text-[9px]' : 'text-[10px]';
  const rowGap = isSmall ? 4 : 8;

  // Colour palette — inverted if light=true
  const textColor = light ? '#0f1611' : '#ffffff';
  const eloDefault = light ? '#3a4a40' : '#d4dad6';
  const eloWinner = '#7ca81f'; // lime that works on both backgrounds
  const sideLabelColor = light ? '#6b766f' : '#94a098';
  const vsColor = light ? '#0f1611' : '#ffffff';
  const vsOpacity = light ? 0.2 : 0.3;

  // Score mode: show the score instead of VS, but keep team-ELO on the sides
  const showScore = score !== null && score.s1 != null && score.s2 != null;
  const t1Won = showScore && score.s1 > score.s2;
  const t2Won = showScore && score.s2 > score.s1;

  // Favourite mode: highest pre-match ELO is green (regardless of whether the match has been played)
  const t1IsFavorite = t1Sum != null && t2Sum != null && t1Sum > t2Sum;
  const t2IsFavorite = t1Sum != null && t2Sum != null && t2Sum > t1Sum;

  // The ELO colour shows the favourite (strongest team before the match)
  const t1EloColor = t1IsFavorite ? eloWinner : eloDefault;
  const t2EloColor = t2IsFavorite ? eloWinner : eloDefault;

  // Render one player cell (group pill + name + optional replacement badge)
  const renderPlayer = (slot, side) => {
    const isRight = side === 'right';
    if (slot.kind !== 'concrete') {
      return (
        <div className={`text-xs italic opacity-50 ${isRight ? 'text-right' : ''}`}>
          {slot.fromGroup
            ? t('tournament.matchup.placeholder', {
                position: String(slot.fromPosition),
                group: String(slot.fromGroup),
              })
            : t('tournament.matchup.tba')}
        </div>
      );
    }
    const player = players?.find((p) => p.id === slot.playerId);
    if (!player) return <div className="text-xs opacity-50">—</div>;
    const colors = slot.originGroup ? groupColor(slot.originGroup) : null;
    const pillSize = isSmall ? 'text-[9px]' : 'text-[10px]';

    const inner = (
      <>
        {colors && !isRight && (
          <span
            className={`font-mono ${pillSize} font-bold px-1 rounded flex-shrink-0`}
            style={{ background: colors.bg, color: colors.text }}
          >
            {slot.originGroup}
          </span>
        )}
        <span className={`truncate ${nameClass}`}>{player.name}</span>
        <ReplacementBadge slot={slot} players={players} darkBg={!light} />
        {colors && isRight && (
          <span
            className={`font-mono ${pillSize} font-bold px-1 rounded flex-shrink-0`}
            style={{ background: colors.bg, color: colors.text }}
          >
            {slot.originGroup}
          </span>
        )}
      </>
    );

    const baseClass = `flex items-center gap-1.5 min-w-0 ${isRight ? 'justify-end' : ''}`;

    if (onPlayerClick) {
      return (
        <button
          onClick={() => onPlayerClick(player.id)}
          className={`${baseClass} bg-transparent border-0 p-0 hover:opacity-80 cursor-pointer w-full ${isRight ? 'text-right' : 'text-left'}`}
          style={{ color: textColor }}
        >
          {inner}
        </button>
      );
    }
    return (
      <div className={baseClass} style={{ color: textColor }}>
        {inner}
      </div>
    );
  };

  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: '1fr auto auto auto 1fr',
        gridTemplateRows: 'auto auto auto',
        rowGap: `${rowGap}px`,
        columnGap: isSmall ? '12px' : '20px',
      }}
    >
      {/* Row 1, column 1: HOME label */}
      <div
        className={`font-mono ${sideLabelClass} uppercase tracking-[0.18em] font-semibold`}
        style={{ gridColumn: 1, gridRow: 1, color: sideLabelColor }}
      >
        {t('tournament.matchup.home')}
      </div>

      {/* Row 1, column 5: AWAY label */}
      <div
        className={`font-mono ${sideLabelClass} uppercase tracking-[0.18em] font-semibold text-right`}
        style={{ gridColumn: 5, gridRow: 1, color: sideLabelColor }}
      >
        {t('tournament.matchup.away')}
      </div>

      {/* Row 2, column 1: player 1 home */}
      <div style={{ gridColumn: 1, gridRow: 2 }}>{renderPlayer(team1.p1, 'left')}</div>

      {/* Rows 2+3, column 2: team1 average ELO + optional ELO change */}
      <div className="text-center" style={{ gridColumn: 2, gridRow: '2 / span 2' }}>
        <div className={`font-mono ${eloClass} font-bold`} style={{ color: t1EloColor }}>
          {t1Sum ?? '—'}
        </div>
        <div
          className={`font-mono ${labelClass} uppercase tracking-wider opacity-50`}
          style={{ color: textColor }}
        >
          {t('tournament.matchup.avgElo')}
        </div>
        {showScore &&
          score.eloChange1 != null &&
          (() => {
            // Show the base change (without boost) in the team pill. The actual
            // boosted change per player is shown in the 2x pill behind the name.
            const t1Mul = score.t1NewbieMul ?? 1.0;
            const t1Unboosted = t1Mul > 0 ? Math.round(score.eloChange1 / t1Mul) : score.eloChange1;
            return (
              <div
                className={`font-mono ${labelClass} font-bold mt-1 inline-block px-1.5 py-0.5 rounded`}
                style={{
                  background:
                    t1Unboosted > 0
                      ? light
                        ? '#dcfce7'
                        : 'rgba(124, 168, 31, 0.25)'
                      : light
                        ? '#fee2e2'
                        : 'rgba(190, 50, 50, 0.25)',
                  color:
                    t1Unboosted > 0
                      ? light
                        ? '#3f6212'
                        : '#a3d957'
                      : light
                        ? '#b91c1c'
                        : '#fda4af',
                }}
              >
                {t1Unboosted > 0 ? '+' : ''}
                {t1Unboosted}
              </div>
            );
          })()}
      </div>

      {/* Rows 2+3, column 3: VS or score */}
      <div className="text-center" style={{ gridColumn: 3, gridRow: '2 / span 2' }}>
        {showScore ? (
          <div className="flex items-center gap-2">
            <span
              className={`font-mono ${scoreClass} font-bold`}
              style={{ color: t1Won ? eloWinner : eloDefault }}
            >
              {score.s1}
            </span>
            <span
              className={`font-display ${isSmall ? 'text-sm' : 'text-xl'} opacity-30`}
              style={{ color: vsColor }}
            >
              –
            </span>
            <span
              className={`font-mono ${scoreClass} font-bold`}
              style={{ color: t2Won ? eloWinner : eloDefault }}
            >
              {score.s2}
            </span>
          </div>
        ) : (
          <div
            className={`font-display ${vsClass} font-bold`}
            style={{ color: vsColor, opacity: vsOpacity }}
          >
            {t('tournament.matchup.vs')}
          </div>
        )}
      </div>

      {/* Rows 2+3, column 4: team2 average ELO + optional ELO change */}
      <div className="text-center" style={{ gridColumn: 4, gridRow: '2 / span 2' }}>
        <div className={`font-mono ${eloClass} font-bold`} style={{ color: t2EloColor }}>
          {t2Sum ?? '—'}
        </div>
        <div
          className={`font-mono ${labelClass} uppercase tracking-wider opacity-50`}
          style={{ color: textColor }}
        >
          {t('tournament.matchup.avgElo')}
        </div>
        {showScore &&
          score.eloChange2 != null &&
          (() => {
            const t2Mul = score.t2NewbieMul ?? 1.0;
            const t2Unboosted = t2Mul > 0 ? Math.round(score.eloChange2 / t2Mul) : score.eloChange2;
            return (
              <div
                className={`font-mono ${labelClass} font-bold mt-1 inline-block px-1.5 py-0.5 rounded`}
                style={{
                  background:
                    t2Unboosted > 0
                      ? light
                        ? '#dcfce7'
                        : 'rgba(124, 168, 31, 0.25)'
                      : light
                        ? '#fee2e2'
                        : 'rgba(190, 50, 50, 0.25)',
                  color:
                    t2Unboosted > 0
                      ? light
                        ? '#3f6212'
                        : '#a3d957'
                      : light
                        ? '#b91c1c'
                        : '#fda4af',
                }}
              >
                {t2Unboosted > 0 ? '+' : ''}
                {t2Unboosted}
              </div>
            );
          })()}
      </div>

      {/* Row 2, column 5: player 1 away */}
      <div style={{ gridColumn: 5, gridRow: 2 }}>{renderPlayer(team2.p1, 'right')}</div>

      {/* Row 3, column 1: player 2 home */}
      <div style={{ gridColumn: 1, gridRow: 3 }}>{renderPlayer(team1.p2, 'left')}</div>

      {/* Row 3, column 5: player 2 away */}
      <div style={{ gridColumn: 5, gridRow: 3 }}>{renderPlayer(team2.p2, 'right')}</div>
    </div>
  );
}
